import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { usePlanningContext } from '../state/PlanningContext';
import { Menu, GripVertical } from 'lucide-react'; // Using Menu icon

const DRAG_THRESHOLD = 5; // Pixels

const HamburgerButton = () => {
  const { isSidebarOpen, setIsSidebarOpen, isApplicationModalOpen } = usePlanningContext();
  const constraintsRef = useRef(null);
  const wasDraggedRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  // Only show the button when sidebar is closed AND modal is not open
  if (isSidebarOpen || isApplicationModalOpen) return null;

  const handleDragStart = () => {
    wasDraggedRef.current = false;
    setIsDragging(true);
  };

  const handleDrag = (event, info) => {
    if (
      Math.abs(info.offset.x) > DRAG_THRESHOLD ||
      Math.abs(info.offset.y) > DRAG_THRESHOLD
    ) {
      wasDraggedRef.current = true;
    }
  };

  const handleTap = () => {
    if (!wasDraggedRef.current) {
      setIsSidebarOpen(true);
    }
    // Reset for the next interaction sequence, regardless of whether sidebar opened.
    // This handles the case where a drag was too small to set wasDraggedRef, but onTap still fired.
    wasDraggedRef.current = false;
  };

  const handleDragEnd = () => {
    // It's generally good practice to reset the flag on drag end as well,
    // ensuring it's clean for any subsequent non-drag taps.
    // However, handleTap also resets it, providing a safety net.
    // If onTap fires immediately after onDragEnd, its check will use the value set by onDrag.
    // If a new tap happens later, wasDraggedRef would have been reset by the previous handleTap or this onDragEnd.
    // For this specific sequence (drag -> release -> tap fires), handleTap checking wasDraggedRef is key.
    // Let's ensure it's reset so a *new, separate* tap later isn't blocked.
    setTimeout(() => {
      wasDraggedRef.current = false;
      setIsDragging(false);
    }, 0); // Timeout to ensure this runs after any potential onTap that might fire nearly simultaneously
  };

  return (
    <>
      {/* Define drag constraints area */}
      <div 
        ref={constraintsRef} 
        className="fixed inset-0 pointer-events-none"
        style={{ zIndex: 999 }} // Ensure it's behind the button but covers the screen for constraints
      />
      <motion.div
        drag
        dragConstraints={constraintsRef}
        dragElastic={0.05} // Reduced elasticity for less "slippery" feel
        dragMomentum={false}
        onDragStart={handleDragStart}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        onTap={handleTap} // onTap handles both click and tap events
        className={`planning-register fixed right-[16px] z-[1000] p-2 border border-[var(--c-border)] bg-[var(--c-surface)]
                    hover:border-[var(--c-text-tertiary)] transition-colors duration-150 ease-in-out select-none drag-handle ${isDragging ? 'dragging' : ''} group`}
        style={{ top: 'calc(var(--navbar-height, 64px) + var(--planning-region-rail-height, 0px) + 0.75rem)' }}
        aria-label="Open menu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3, duration: 0.2 }}
      >
        <div className="flex items-center gap-1.5 pointer-events-none"> {/* Prevent child elements from interfering with drag/tap on parent */}
          <GripVertical size={16} className="text-[var(--c-text-tertiary)] group-hover:text-[var(--c-text-secondary)] transition-colors" />
          <Menu size={18} className="text-[var(--c-text-secondary)] group-hover:text-[var(--c-text-primary)] transition-colors" />
          <span className="ml-1 font-['DM_Mono'] text-[10px] uppercase tracking-[0.1em] text-[var(--c-text-tertiary)]">Menu</span>
        </div>
      </motion.div>
    </>
  );
};

export default HamburgerButton;
